import { Fields } from "../../types/SchemaFactory";
import { TFunction } from "i18next";
import { schemaFactory } from "../SchemaFactory";
import {
    WorkCreationFirstStep,
    WorkCreationSecondStep,
} from "../../domain/models/dto/WorkDTO";
import { phoneInfoFields } from "../PhoneInfoSchema";
import { InitialPhone } from "../../domain/models/Phone";
import {
    getCurrentDatetimeStr,
    getCurrentDatetimeWithCorrectionMin,
    getCurrentDatetimeWithCorrectionYears,
} from "../../tools/DatetimeTools";
import { getMaxEnumValue } from "../../tools/EnumTools";
import { WorkCategory } from "../../domain/enums/WorkCategoryEnum";
import { worksCategoriesFields } from "./WorksCategoriesSchema";
import { MINUTES_IN_DAY } from "../../domain/constants";
import { workStartFromOffsetMin } from "../../Config";
import { priceFields } from "../PriceSchema";
import { addressFieldsFactory } from "../AddressSchema";

export const workCreationFirstStepFields: Fields<WorkCreationFirstStep> = {
    title: {
        min: 2,
        max: 100,
        placeholderKey: "workCreation.fields.titlePlaceholder",
        required: true,
        type: "text",
        initial: "",
    },
    categories: {
        min: 1,
        max: getMaxEnumValue(WorkCategory),
        required: true,
        placeholderKey: "",
        type: "schema",
        schema: worksCategoriesFields,
        array: true,
        initial: [],
    },
    price: {
        min: 1,
        max: 1,
        required: true,
        type: "schema",
        schema: priceFields,
        initial: null,
        placeholderKey: "",
    },
    estimatedHours: {
        min: 1,
        max: {
            value: 300,
            key: "workCreation.fields.max.estimatedHours",
        },
        required: true,
        placeholderKey: "workCreation.fields.timeOfExecutionPlaceholder",
        type: "number",
        initial: null,
    },
    workersAmount: {
        min: 1,
        max: {
            value: 100,
            key: "workCreation.fields.max.workersAmount",
        },
        required: true,
        placeholderKey: "workCreation.fields.numberOfWorkersPlaceholder",
        type: "number",
        initial: null,
    },
    address: {
        min: 1,
        max: 1,
        required: true,
        type: "schema",
        schema: addressFieldsFactory(),
        initial: null,
        placeholderKey: "",
    },
    description: {
        min: 2,
        max: 500,
        required: true,
        placeholderKey: "workCreation.fields.jobDescriptionPlaceholder",
        type: "text",
        initial: "",
    },
};

export const workCreationSecondStepFields: Fields<WorkCreationSecondStep> = {
    startFrom: {
        min: () => ({
            value: getCurrentDatetimeWithCorrectionMin(-workStartFromOffsetMin),
            key: "workCreation.fields.min.startFrom",
        }),
        max: () => ({
            value: getCurrentDatetimeWithCorrectionYears(1),
            key: "workCreation.fields.max.startFrom",
        }),
        required: true,
        placeholderKey: "",
        type: "datetime-local",
        initial: getCurrentDatetimeStr(),
    },
    showContactsForAll: {
        min: 0,
        max: 1,
        required: true,
        placeholderKey: "workCreation.fields.radiobutton1",
        type: "boolean",
        initial: true,
    },
    showContactsAfterSelect: {
        min: 0,
        max: 1,
        required: true,
        placeholderKey: "workCreation.fields.radiobutton2",
        type: "boolean",
        initial: false,
    },
    expiresInMinutes: {
        min: 1,
        max: {
            value: MINUTES_IN_DAY,
            key: "workCreation.fields.max.expiresInMinutes",
        },
        required: false,
        placeholderKey: "workCreation.fields.radiobutton1Placeholder",
        type: "number",
        initial: null,
    },
    telegram: {
        min: 0,
        max: 33,
        required: false,
        placeholderKey: "workCreation.fields.telegramField",
        type: "text",
        pattern: /^@?[a-zA-Z0-9_]{5,32}$/,
        patternErrorKey: "errors.validation.invalidTelegram",
        initial: "",
    },
    phones: {
        min: 1,
        max: 2,
        required: true,
        placeholderKey: "",
        type: "schema",
        schema: phoneInfoFields,
        array: true,
        initial: [InitialPhone, InitialPhone],
    },
};

export const workCreationFirstStepSchemaFactory = (t: TFunction) => {
    return schemaFactory(t, workCreationFirstStepFields);
};

export const workCreationSecondStepSchemaFactory = (t: TFunction) => {
    return schemaFactory(t, workCreationSecondStepFields);
};
